import { ChooseDate } from 'app/src/components/ChooseDate';
import momentDefault from 'moment';
import PopOver from 'app/scripts/views/lib/pop-over.coffee';
import React from 'react';
import Util from 'app/scripts/lib/util/index.coffee';
import { trackUe, Noun, Verb } from '@trello/analytics';
import { AdvancedChecklistsBetaInfo } from './advanced-checklists-beta-info';

interface OptionsV2 {
  title: string;
  trackingMethod: string;
  // In V2, we can set the date directly, rather than embedding it in the
  // checkItem name.
  getInitialDate: () => string | undefined;
  setDate: (value: Date | null) => void;
  moment?: typeof momentDefault.utc;
}

export function toggleDatePopoverV2({
  title,
  trackingMethod,
  getInitialDate,
  setDate,
  moment = momentDefault,
}: OptionsV2) {
  return function toggle(e: MouseEvent) {
    Util.stop(e);

    const dateString = getInitialDate() || undefined;
    const dateInitial = dateString ? moment(dateString).toDate() : undefined;

    trackUe(
      Noun.CARD_DETAIL,
      Verb.OPENS,
      dateInitial ? Noun.CHANGE_DUE_DATE_PICKER : Noun.DUE_DATE_PICKER,
      'on checkitem',
      '',
      trackingMethod,
    );

    PopOver.toggle({
      elem: e.currentTarget,
      getViewTitle: () => title,
      keepEdits: true,
      reactElement: (
        <React.Fragment key="choose-date">
          <ChooseDate
            dateInitial={dateInitial}
            onSelect={(date) => {
              PopOver.hide();

              // Since the user doesn't pick a time, and the time isn't visible
              // shown, set the time to 11:59pm. That way, the checkItem won't
              // appear overdue until the next day.
              const endOfDay = date
                ? moment(date)
                    .hour(23)
                    .minute(59)
                    .toDate()
                : null;

              setDate(endOfDay);
            }}
          />
        </React.Fragment>
      ),
    });
  };
}

interface OptionsBeta {
  title: string;
  trackingMethod: string;
  getInitialDate?: () => string | null;
  // In beta, we have to indirectly set the date by embedding it in the
  // checkItem name.
  getInitialName: () => string;
  setValue: (value: string) => void;
  moment?: typeof momentDefault.utc;
}

function bind<T extends (...args: unknown[]) => unknown>(
  fn: T,
  context: unknown,
): T {
  return fn.bind(context);
}

export function toggleDatePopoverBeta(options: OptionsBeta) {
  const { title, trackingMethod, moment = momentDefault } = options;

  return function toggle(e: MouseEvent) {
    Util.stop(e);

    const setValue = bind(options.setValue, this);
    const getInitialName = bind(options.getInitialName, this);

    const initialName = getInitialName();
    const dateStringInitial = options.getInitialDate
      ? options.getInitialDate.call(this)
      : undefined;
    let dateInitial = dateStringInitial
      ? moment(dateStringInitial).toDate()
      : undefined;

    if (!dateInitial) {
      const dateMatch = initialName.match(/\b\d\d\d\d-\d\d-\d\d$/);
      const existingDueDate = dateMatch && dateMatch[0];
      const momentDueDate = existingDueDate
        ? moment(existingDueDate)
        : undefined;
      dateInitial =
        momentDueDate && momentDueDate.isValid()
          ? momentDueDate.toDate()
          : undefined;
    }

    trackUe(
      Noun.CARD_DETAIL,
      Verb.OPENS,
      dateInitial ? Noun.CHANGE_DUE_DATE_PICKER : Noun.DUE_DATE_PICKER,
      'on checkitem',
      '',
      trackingMethod,
    );

    PopOver.toggle({
      elem: e.currentTarget,
      getViewTitle: () => title,
      keepEdits: true,
      reactElement: (
        <React.Fragment key="choose-date">
          <AdvancedChecklistsBetaInfo />
          <ChooseDate
            {...{ dateInitial }}
            onSelect={(date) => {
              PopOver.hide();

              let dateString = '';

              if (date) {
                const formattedDate = moment(date).format('YYYY-MM-DD');

                dateString = /\s$/.test(initialName)
                  ? formattedDate
                  : ` ${formattedDate}`;
              }

              const newValue = dateInitial
                ? initialName.replace(/\s?\d\d\d\d-\d\d-\d\d$|$/, dateString)
                : initialName + dateString;

              setValue(newValue);
            }}
          />
        </React.Fragment>
      ),
    });
  };
}

export function replaceDueInputValue(
  input: HTMLInputElement | HTMLTextAreaElement,
  newValue: string,
) {
  const initialValue = input.value;
  const selectionStart = input.selectionStart;
  const selectionEnd = input.selectionEnd;

  input.value = newValue;
  if (selectionStart !== null && selectionEnd !== null) {
    const newSelectionStart = selectionStart;
    const newSelectionEnd =
      selectionStart === 0 &&
      selectionEnd === initialValue.length &&
      selectionEnd > selectionStart
        ? // If they've got everything selected, keep everything selected
          newValue.length
        : selectionEnd;

    input.setSelectionRange(newSelectionStart, newSelectionEnd);
  }

  input.focus();
}
